import { matchIsValidTel, MuiTelInput } from 'mui-tel-input';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Alert, Button, Card, CardContent, TextField, Typography } from '@/ui';

import {
  formatMfaErrorMessage,
  getMfaErrorType,
  shouldSkipSupportNudge,
  shouldTriggerTotpReenrollment,
  stytchErrorMessage,
  withSupportNudge,
} from './stytchErrors';

import type { MfaMethod } from '@/store/slices/mfaSlice';

import {
  useGetAuthStatusQuery,
  useMfaSmsAuthenticateMutation,
  useMfaSmsSendMutation,
  useMfaSmsSetPhoneMutation,
  useMfaTotpAuthenticateMutation,
  useMfaTotpCreateMutation,
} from '@/store/api/authApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  clearErrorMessage,
  resetSmsState,
  setBusy,
  setErrorMessage,
  setMethod,
  setOtpCode,
  setPhone,
  setSmsAlreadyHaveCode,
  setSmsMethodId,
  setTotpCode,
  setTotpId,
  setTotpQrCode,
  setTotpRecoveryCodes,
} from '@/store/slices/mfaSlice';
import {
  normalizePostAuthRedirect,
  redirectToSignin,
} from '@/utils/authRedirect';
import { track } from '@/utils/tracking';

const MFA_PROMPT_SKIPPED_UNTIL_KEY = 'mfa_prompt_skipped_until';
const UNSUPPORTED_SMS_COUNTRY_HELPER =
  'SMS isn’t available for this country. Use an authenticator app instead, or sign in with Google.';

type SubtitleArgs = {
  method: MfaMethod;
  smsMethodId: string | null;
  smsPhoneOnFile: boolean;
};

const getSubtitle = ({
  method,
  smsMethodId,
  smsPhoneOnFile,
}: SubtitleArgs): string => {
  if (method === 'totp') return 'Use an authenticator app to verify a code.';
  if (smsMethodId) return 'Enter the verification code we sent to your phone.';
  if (smsPhoneOnFile) return 'Send a verification code to your phone on file.';
  return 'Enter your phone number to save it and receive a verification code.';
};

type SmsBannerArgs = {
  smsAlreadyHaveCode: boolean;
  smsMasked?: string | null;
  smsMethodId: string | null;
  smsPhoneVerified?: boolean;
};

const renderSmsBanner = ({
  smsAlreadyHaveCode,
  smsMasked,
  smsMethodId,
  smsPhoneVerified,
}: SmsBannerArgs) => {
  const masked = smsMasked || 'your phone';

  if (smsMethodId) {
    return (
      <>
        Code sent to <strong>{masked}</strong>. It may take a moment to arrive.
      </>
    );
  }

  if (smsAlreadyHaveCode) {
    return (
      <>
        Enter the code you already have for <strong>{masked}</strong>.
      </>
    );
  }

  return (
    <>
      We’ll send a code to <strong>{masked}</strong>.
      {!smsPhoneVerified ? <> (this number will be verified for MFA)</> : null}
    </>
  );
};

const formatGraceExpiresAt = (iso?: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

type SmsActionButtonsProps = {
  busy: 'send' | 'verify' | null;
  canSend: boolean;
  canVerifySms: boolean;
  mfaSmsAuthenticateResult: { isLoading: boolean };
  mfaSmsSendResult: { isLoading: boolean };
  mfaSmsSetPhoneResult: { isLoading: boolean };
  onResetSmsState: () => void;
  onSavePhone: () => void;
  onSendSms: () => void;
  onSetAlreadyHaveCode: () => void;
  onSetErrorMessage: (message: string | null) => void;
  onSetOtpCode: (code: string) => void;
  onSetSmsAlreadyHaveCode: (value: boolean) => void;
  onVerifySms: () => void;
  otpCode: string;
  smsAlreadyHaveCode: boolean;
  smsMethodId: string | null;
  smsPhoneOnFile: boolean;
};

const renderSmsActionButtons = ({
  busy,
  canSend,
  canVerifySms,
  mfaSmsAuthenticateResult,
  mfaSmsSendResult,
  mfaSmsSetPhoneResult,
  onResetSmsState,
  onSavePhone,
  onSendSms,
  onSetAlreadyHaveCode,
  onSetErrorMessage,
  onSetOtpCode,
  onSetSmsAlreadyHaveCode,
  onVerifySms,
  otpCode,
  smsAlreadyHaveCode,
  smsMethodId,
  smsPhoneOnFile,
}: SmsActionButtonsProps) => {
  if (!smsPhoneOnFile) {
    return (
      <Button
        disabled={!canSend}
        loading={busy === 'send' || mfaSmsSetPhoneResult.isLoading}
        variant="contained"
        onClick={onSavePhone}
      >
        Save phone &amp; continue
      </Button>
    );
  }

  if (smsMethodId) {
    return (
      <>
        <TextField
          inputProps={{ inputMode: 'numeric' }}
          label="Verification code"
          placeholder="123456"
          value={otpCode}
          fullWidth
          onChange={(e) => onSetOtpCode(e.target.value)}
        />

        <Button
          disabled={!canVerifySms}
          loading={busy === 'verify' || mfaSmsAuthenticateResult.isLoading}
          variant="contained"
          onClick={onVerifySms}
        >
          Verify
        </Button>

        <Button color="gray" variant="outlined" onClick={onResetSmsState}>
          Send a new code
        </Button>
      </>
    );
  }

  if (smsAlreadyHaveCode) {
    return (
      <>
        <TextField
          inputProps={{ inputMode: 'numeric' }}
          label="Verification code"
          placeholder="123456"
          value={otpCode}
          fullWidth
          onChange={(e) => onSetOtpCode(e.target.value)}
        />

        <Button
          disabled={!canVerifySms}
          loading={busy === 'verify' || mfaSmsAuthenticateResult.isLoading}
          variant="contained"
          onClick={onVerifySms}
        >
          Verify
        </Button>

        <Button
          color="gray"
          variant="outlined"
          onClick={() => {
            onSetSmsAlreadyHaveCode(false);
            onSetOtpCode('');
            onSetErrorMessage(null);
          }}
        >
          Back
        </Button>
      </>
    );
  }

  return (
    <>
      <Button
        loading={busy === 'send' || mfaSmsSendResult.isLoading}
        variant="contained"
        onClick={onSendSms}
      >
        Send code
      </Button>

      <button
        className="text-sm underline opacity-80"
        type="button"
        onClick={onSetAlreadyHaveCode}
      >
        Already have a code?
      </button>
    </>
  );
};

const handleSkipMfa = (
  authStatus: { graceExpiresAt?: string | null } | undefined,
  navigate: (path: string, options?: { replace: boolean }) => void,
  normalizedRedirect: string,
) => {
  try {
    const until = authStatus?.graceExpiresAt
      ? Date.parse(authStatus.graceExpiresAt)
      : Date.now() + 24 * 60 * 60 * 1000;
    window.localStorage.setItem(MFA_PROMPT_SKIPPED_UNTIL_KEY, String(until));
  } catch {
    // ignore
  }
  navigate(normalizedRedirect, { replace: true });
};

type TotpSectionProps = {
  busy: 'send' | 'verify' | null;
  canVerifyTotp: boolean;
  mfaTotpAuthenticateResult: { isLoading: boolean };
  mfaTotpCreateResult: { isLoading: boolean };
  onSetTotpCode: (code: string) => void;
  onStartTotpSetup: () => void;
  onVerifyTotp: () => void;
  qrSrc: string | null;
  totpCode: string;
  totpConfigured: boolean;
  totpRecoveryCodes: string[] | null;
};

const renderTotpSection = ({
  busy,
  canVerifyTotp,
  mfaTotpAuthenticateResult,
  mfaTotpCreateResult,
  onSetTotpCode,
  onStartTotpSetup,
  onVerifyTotp,
  qrSrc,
  totpCode,
  totpConfigured,
  totpRecoveryCodes,
}: TotpSectionProps) => {
  if (!totpConfigured && !qrSrc) {
    return (
      <Button
        loading={busy === 'send' || mfaTotpCreateResult.isLoading}
        variant="contained"
        onClick={onStartTotpSetup}
      >
        Set up authenticator app
      </Button>
    );
  }

  return (
    <>
      {qrSrc && (
        <div className="mb-4 flex justify-center">
          <img alt="Scan QR code" className="size-48" src={qrSrc} />
        </div>
      )}

      {totpRecoveryCodes?.length ? (
        <Alert className="mb-4" severity="info">
          Save these recovery codes somewhere safe:
          <ul className="mt-2 list-disc pl-6">
            {totpRecoveryCodes.map((c) => (
              <li key={c}>
                <code>{c}</code>
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <TextField
        inputProps={{ inputMode: 'numeric' }}
        label="Authenticator code"
        placeholder="123456"
        value={totpCode}
        fullWidth
        onChange={(e) => onSetTotpCode(e.target.value)}
      />

      <div className="mt-6 flex flex-col gap-3">
        <Button
          disabled={!canVerifyTotp}
          loading={busy === 'verify' || mfaTotpAuthenticateResult.isLoading}
          variant="contained"
          onClick={onVerifyTotp}
        >
          Verify
        </Button>
      </div>
    </>
  );
};

const Mfa = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const redirect = search.get('redirect');
  const trackedView = useRef(false);

  const {
    data: authStatus,
    error: authStatusError,
    isLoading: isAuthStatusLoading,
    refetch: refetchAuthStatus,
  } = useGetAuthStatusQuery();

  const [mfaSmsSetPhone, mfaSmsSetPhoneResult] = useMfaSmsSetPhoneMutation();
  const [mfaSmsSend, mfaSmsSendResult] = useMfaSmsSendMutation();
  const [mfaSmsAuthenticate, mfaSmsAuthenticateResult] =
    useMfaSmsAuthenticateMutation();
  const [mfaTotpCreate, mfaTotpCreateResult] = useMfaTotpCreateMutation();
  const [mfaTotpAuthenticate, mfaTotpAuthenticateResult] =
    useMfaTotpAuthenticateMutation();

  // Redux state
  const method = useAppSelector((state) => state.mfa.method);
  const phoneValue = useAppSelector((state) => state.mfa.phoneValue);
  const phoneE164 = useAppSelector((state) => state.mfa.phoneE164);
  const phoneCountryIso2 = useAppSelector(
    (state) => state.mfa.phoneCountryIso2,
  );
  const otpCode = useAppSelector((state) => state.mfa.otpCode);
  const smsMethodId = useAppSelector((state) => state.mfa.smsMethodId);
  const smsAlreadyHaveCode = useAppSelector(
    (state) => state.mfa.smsAlreadyHaveCode,
  );
  const busy = useAppSelector((state) => state.mfa.busy);
  const errorMessage = useAppSelector((state) => state.mfa.errorMessage);
  const totpId = useAppSelector((state) => state.mfa.totpId);
  const totpQrCode = useAppSelector((state) => state.mfa.totpQrCode);
  const totpRecoveryCodes = useAppSelector(
    (state) => state.mfa.totpRecoveryCodes,
  );
  const totpCode = useAppSelector((state) => state.mfa.totpCode);

  const normalizedRedirect = useMemo(
    () =>
      normalizePostAuthRedirect(redirect, {
        extraBlockedPrefixes: ['/mfa', '/verify-email'],
      }),
    [redirect],
  );

  const supportedSmsCountryIsoSet = useMemo(() => {
    const list = authStatus?.smsSupportedCountryIsos;
    if (!Array.isArray(list) || list.length === 0) return null;
    return new Set(list.map((c) => String(c).toUpperCase()));
  }, [authStatus?.smsSupportedCountryIsos]);

  const smsCountryUnsupported =
    supportedSmsCountryIsoSet &&
    phoneCountryIso2 &&
    !supportedSmsCountryIsoSet.has(phoneCountryIso2.toUpperCase());

  const phoneValid = phoneValue ? matchIsValidTel(phoneValue) : false;
  const canSend =
    phoneValid && phoneE164.startsWith('+') && !smsCountryUnsupported;

  const smsPhoneHelperText = useMemo(() => {
    if (smsCountryUnsupported) return UNSUPPORTED_SMS_COUNTRY_HELPER;
    if (phoneValue && !phoneValid) return 'Enter a valid phone number.';
    return 'We’ll save this number to your profile and send a code via SMS.';
  }, [phoneValue, phoneValid, smsCountryUnsupported]);

  const canVerifySms =
    (smsAlreadyHaveCode || !!smsMethodId) && otpCode.trim().length >= 4;
  const smsPhoneOnFile = authStatus?.smsPhoneOnFile === true;

  const totpConfigured = authStatus?.totpConfigured === true;
  const canVerifyTotp = totpCode.trim().length >= 4;
  const smsConfigured = authStatus?.smsPhoneVerified === true;
  const mfaConfigured = totpConfigured || smsConfigured;
  const canSkip =
    authStatus?.mfaCanSkip === true &&
    !authStatus.mfaSatisfied &&
    !mfaConfigured;

  const configuredMethod: MfaMethod | null = useMemo(() => {
    // If the user has already enrolled in any method, lock them to that method (no switching).
    if (!totpConfigured && !smsConfigured) return null;

    if (authStatus.mfaDefaultMethod === 'totp') return 'totp';
    if (authStatus.mfaDefaultMethod === 'sms') return 'sms';

    // Fallback: prefer TOTP if configured, otherwise SMS.
    return totpConfigured ? 'totp' : 'sms';
  }, [authStatus?.mfaDefaultMethod, smsConfigured, totpConfigured]);

  const availableMethods: MfaMethod[] = useMemo(
    () => (configuredMethod ? [configuredMethod] : ['sms', 'totp']),
    [configuredMethod],
  );

  const canSelectMethod = availableMethods.length > 1;

  useEffect(() => {
    if (trackedView.current) return;
    if (isAuthStatusLoading) return;
    if (!authStatus) return;
    trackedView.current = true;
    track('auth_mfa_viewed', {
      configured_method: configuredMethod,
      mfa_default_method: authStatus.mfaDefaultMethod,
      mfa_prompt: authStatus.mfaPrompt,
      mfa_required: authStatus.mfaRequired,
      redirect: normalizedRedirect,
    });
  }, [authStatus, configuredMethod, isAuthStatusLoading, normalizedRedirect]);

  useEffect(() => {
    if (isAuthStatusLoading) return;

    const err = authStatusError as { status?: number } | undefined;
    if (err?.status === 401) {
      const currentPath = window.location.pathname + window.location.search;
      redirectToSignin(currentPath);
      return;
    }

    if (!authStatus) return;

    // If the user already has MFA enrolled, only show that option and force the tab selection.
    if (configuredMethod) {
      dispatch(setMethod(configuredMethod));
    } else if (authStatus.mfaDefaultMethod === 'totp') {
      dispatch(setMethod('totp'));
    }

    // If MFA is not required and there's no prompt, leave /mfa.
    if (!authStatus.mfaRequired && !authStatus.mfaPrompt) {
      navigate(normalizedRedirect, { replace: true });
    }
  }, [
    authStatus,
    authStatusError,
    configuredMethod,
    dispatch,
    isAuthStatusLoading,
    navigate,
    normalizedRedirect,
  ]);

  const selectMethod = (nextMethod: MfaMethod) => {
    track('auth_mfa_method_selected', {
      mfa_method: nextMethod,
      redirect: normalizedRedirect,
    });
    dispatch(setMethod(nextMethod));
  };

  const savePhone = async () => {
    dispatch(clearErrorMessage());
    if (smsCountryUnsupported) {
      dispatch(
        setErrorMessage(withSupportNudge(UNSUPPORTED_SMS_COUNTRY_HELPER)),
      );
      return;
    }
    if (!phoneValid || !phoneE164.startsWith('+')) {
      dispatch(setErrorMessage('Enter a valid phone number.'));
      return;
    }

    dispatch(setBusy('send'));
    try {
      await mfaSmsSetPhone({
        countryIso2: phoneCountryIso2,
        phone: phoneE164,
      }).unwrap();
      dispatch(setSmsMethodId(null));
      dispatch(setOtpCode(''));
      await refetchAuthStatus();
      track('auth_mfa_sms_phone_saved_success', {
        redirect: normalizedRedirect,
      });
    } catch (e) {
      track('auth_mfa_sms_phone_saved_failed', {
        http_status: (e as { status?: number } | undefined)?.status,
        redirect: normalizedRedirect,
      });
      dispatch(setErrorMessage(formatMfaErrorMessage(e)));
    } finally {
      dispatch(setBusy(null));
    }
  };

  const sendSms = async () => {
    dispatch(clearErrorMessage());
    try {
      dispatch(setBusy('send'));
      const resp = await mfaSmsSend().unwrap();
      const methodId =
        (resp as unknown as { methodId?: string; method_id?: string })
          .methodId ??
        (resp as unknown as { methodId?: string; method_id?: string })
          .method_id;
      if (!methodId) {
        dispatch(
          setErrorMessage(
            withSupportNudge(
              'We couldn’t send a verification code. Please try again.',
            ),
          ),
        );
        return;
      }
      dispatch(setOtpCode(''));
      dispatch(setSmsMethodId(methodId));
      dispatch(setSmsAlreadyHaveCode(false));
      track('auth_mfa_sms_send_success', {
        redirect: normalizedRedirect,
      });
    } catch (e) {
      const errorType = getMfaErrorType(e);
      if (
        errorType === 'email_verification_required' ||
        errorType === 'mfa_verification_required' ||
        errorType === 'too_many_unverified_factors'
      ) {
        const redirectParam =
          normalizedRedirect && normalizedRedirect !== '/'
            ? `?redirect=${encodeURIComponent(normalizedRedirect)}`
            : '';
        navigate(`/verify-email${redirectParam}`, { replace: true });
        return;
      }
      track('auth_mfa_sms_send_failed', {
        error_type: errorType,
        http_status: (e as { status?: number } | undefined)?.status,
        redirect: normalizedRedirect,
      });
      dispatch(setErrorMessage(formatMfaErrorMessage(e)));
    } finally {
      dispatch(setBusy(null));
    }
  };

  const verifySms = async () => {
    dispatch(clearErrorMessage());
    if (!canVerifySms) {
      dispatch(setErrorMessage('Enter the code you received via SMS.'));
      return;
    }

    dispatch(setBusy('verify'));
    try {
      await mfaSmsAuthenticate({
        // When the user clicks "Already have a code?", we may not have a current method id.
        // Backend can fall back to the last-sent sms phone id on the profile.
        methodId: smsMethodId ?? undefined,
        otpCode: otpCode.trim(),
      }).unwrap();

      try {
        window.localStorage.removeItem(MFA_PROMPT_SKIPPED_UNTIL_KEY);
      } catch {
        // ignore
      }

      // Confirm backend sees the new session as MFA-satisfied before leaving /mfa.
      const status = await refetchAuthStatus().unwrap();
      if (status.mfaRequired || status.mfaPrompt) {
        dispatch(
          setErrorMessage(
            withSupportNudge(
              'We verified your code, but MFA is still required. Please refresh and try again.',
            ),
          ),
        );
        return;
      }

      track('auth_mfa_sms_verify_success', {
        redirect: normalizedRedirect,
      });
      navigate(normalizedRedirect, { replace: true });
    } catch (e) {
      const msg = stytchErrorMessage(e);
      const errorType = getMfaErrorType(e);
      const maybeInvalidRequest =
        errorType === 'invalid_request' ||
        msg.includes('invalid_request') ||
        msg.includes('invalid request');
      const finalMessage = maybeInvalidRequest
        ? 'We couldn’t verify that code. Please request a new code and try again.'
        : msg;
      dispatch(
        setErrorMessage(
          shouldSkipSupportNudge(e)
            ? finalMessage
            : withSupportNudge(finalMessage),
        ),
      );
      track('auth_mfa_sms_verify_failed', {
        error_type: errorType,
        http_status: (e as { status?: number } | undefined)?.status,
        redirect: normalizedRedirect,
      });
    } finally {
      dispatch(setBusy(null));
    }
  };

  const startTotpSetup = async (options?: { reenrollment?: boolean }) => {
    dispatch(clearErrorMessage());
    dispatch(setBusy('send'));
    try {
      const shouldReenroll =
        options?.reenrollment === true ||
        (authStatus?.b2bMigrationActive === true && !totpConfigured);
      const resp = await mfaTotpCreate(
        shouldReenroll ? { reenrollment: true } : undefined,
      ).unwrap();
      dispatch(setTotpId(resp.totpId));
      dispatch(setTotpQrCode(resp.qrCode));
      dispatch(setTotpRecoveryCodes(resp.recoveryCodes ?? null));
      track('auth_mfa_totp_setup_success', {
        redirect: normalizedRedirect,
      });
    } catch (e) {
      const errorType = getMfaErrorType(e);
      if (
        errorType === 'email_verification_required' ||
        errorType === 'mfa_verification_required' ||
        errorType === 'too_many_unverified_factors'
      ) {
        const redirectParam =
          normalizedRedirect && normalizedRedirect !== '/'
            ? `?redirect=${encodeURIComponent(normalizedRedirect)}`
            : '';
        navigate(`/verify-email${redirectParam}`, { replace: true });
        return;
      }
      track('auth_mfa_totp_setup_failed', {
        error_type: errorType,
        http_status: (e as { status?: number } | undefined)?.status,
        redirect: normalizedRedirect,
      });
      dispatch(setErrorMessage(formatMfaErrorMessage(e)));
    } finally {
      dispatch(setBusy(null));
    }
  };

  const verifyTotp = async () => {
    dispatch(clearErrorMessage());
    if (!canVerifyTotp) {
      dispatch(setErrorMessage('Enter the code from your authenticator app.'));
      return;
    }

    dispatch(setBusy('verify'));
    try {
      await mfaTotpAuthenticate({
        totpCode: totpCode.trim(),
        totpId: totpId ?? undefined,
      }).unwrap();

      try {
        window.localStorage.removeItem(MFA_PROMPT_SKIPPED_UNTIL_KEY);
      } catch {
        // ignore
      }

      const status = await refetchAuthStatus().unwrap();
      if (status.mfaRequired || status.mfaPrompt) {
        dispatch(
          setErrorMessage(
            withSupportNudge(
              'We verified your authenticator code, but MFA is still required. Please refresh and try again.',
            ),
          ),
        );
        return;
      }

      track('auth_mfa_totp_verify_success', {
        redirect: normalizedRedirect,
      });
      navigate(normalizedRedirect, { replace: true });
    } catch (e) {
      const errorType = getMfaErrorType(e);

      // TOTP not configured in the current Stytch project (e.g. after B2C to B2B migration).
      // Auto-trigger the TOTP setup flow so the user can re-enroll.
      if (shouldTriggerTotpReenrollment(e)) {
        track('auth_mfa_totp_reenrollment_triggered', {
          error_type: errorType,
          redirect: normalizedRedirect,
        });
        dispatch(setTotpCode(''));
        await startTotpSetup({ reenrollment: true });
        return;
      }

      track('auth_mfa_totp_verify_failed', {
        error_type: errorType,
        http_status: (e as { status?: number } | undefined)?.status,
        redirect: normalizedRedirect,
      });
      dispatch(setErrorMessage(formatMfaErrorMessage(e)));
    } finally {
      dispatch(setBusy(null));
    }
  };

  const qrSrc = useMemo(() => {
    if (!totpQrCode) return null;
    return totpQrCode.startsWith('data:')
      ? totpQrCode
      : `data:image/png;base64,${totpQrCode}`;
  }, [totpQrCode]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-sage-100 px-6 py-10">
      <Card className="w-full max-w-xl">
        <CardContent>
          <Typography className="text-center" variant="h6" gutterBottom>
            Multi-factor authentication
          </Typography>
          <Typography className="mb-6 text-center" variant="body2">
            {getSubtitle({ method, smsMethodId, smsPhoneOnFile })}
          </Typography>

          {canSkip && (
            <Alert className="mb-4" severity="info">
              You can set this up now, or skip and do it later. MFA will be
              required after the grace period ends.
            </Alert>
          )}

          {canSkip && authStatus.graceExpiresAt && (
            <Typography className="mb-4" variant="body2">
              Grace period ends:{' '}
              <strong>{formatGraceExpiresAt(authStatus.graceExpiresAt)}</strong>
            </Typography>
          )}

          {errorMessage && (
            <Alert className="mb-4" severity="error">
              {errorMessage}
            </Alert>
          )}

          {canSelectMethod ? (
            <div className="mb-6 flex gap-3">
              {availableMethods.includes('sms') && (
                <Button
                  variant={method === 'sms' ? 'contained' : 'outlined'}
                  onClick={() => selectMethod('sms')}
                >
                  Text message
                </Button>
              )}
              {availableMethods.includes('totp') && (
                <Button
                  variant={method === 'totp' ? 'contained' : 'outlined'}
                  onClick={() => selectMethod('totp')}
                >
                  Authenticator app
                </Button>
              )}
            </div>
          ) : null}

          {method === 'sms' ? (
            <>
              {!smsPhoneOnFile ? (
                <MuiTelInput
                  defaultCountry="US"
                  error={smsCountryUnsupported || (!!phoneValue && !phoneValid)}
                  helperText={smsPhoneHelperText}
                  label="Phone number"
                  placeholder="Phone number"
                  value={phoneValue}
                  autoFocus
                  forceCallingCode
                  fullWidth
                  onChange={(value, info) => {
                    dispatch(
                      setPhone({
                        phoneCountryIso2: info.countryCode || null,
                        phoneE164: info.numberValue || '',
                        phoneValue: value || '',
                      }),
                    );
                  }}
                />
              ) : (
                <Alert className="mb-4" severity="info">
                  {renderSmsBanner({
                    smsAlreadyHaveCode,
                    smsMasked: authStatus.smsMasked,
                    smsMethodId,
                    smsPhoneVerified: authStatus.smsPhoneVerified,
                  })}
                </Alert>
              )}

              <div className="mt-6 flex flex-col gap-3">
                {renderSmsActionButtons({
                  busy,
                  canSend,
                  canVerifySms,
                  mfaSmsAuthenticateResult,
                  mfaSmsSendResult,
                  mfaSmsSetPhoneResult,
                  onResetSmsState: () => {
                    dispatch(resetSmsState());
                  },
                  onSavePhone: savePhone,
                  onSendSms: sendSms,
                  onSetAlreadyHaveCode: () => {
                    dispatch(clearErrorMessage());
                    dispatch(setOtpCode(''));
                    dispatch(setSmsAlreadyHaveCode(true));
                  },
                  onSetErrorMessage: (message: string | null) => {
                    dispatch(setErrorMessage(message));
                  },
                  onSetOtpCode: (code: string) => {
                    dispatch(setOtpCode(code));
                  },
                  onSetSmsAlreadyHaveCode: (value: boolean) => {
                    dispatch(setSmsAlreadyHaveCode(value));
                  },
                  onVerifySms: verifySms,
                  otpCode,
                  smsAlreadyHaveCode,
                  smsMethodId,
                  smsPhoneOnFile,
                })}

                {canSkip && (
                  <Button
                    color="gray"
                    variant="outlined"
                    onClick={() => {
                      track('auth_mfa_skipped', {
                        redirect: normalizedRedirect,
                      });
                      handleSkipMfa(authStatus, navigate, normalizedRedirect);
                    }}
                  >
                    Skip for now
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              {renderTotpSection({
                busy,
                canVerifyTotp,
                mfaTotpAuthenticateResult,
                mfaTotpCreateResult,
                onSetTotpCode: (code: string) => {
                  dispatch(setTotpCode(code));
                },
                onStartTotpSetup: startTotpSetup,
                onVerifyTotp: verifyTotp,
                qrSrc,
                totpCode,
                totpConfigured,
                totpRecoveryCodes,
              })}

              <div className="mt-6 flex flex-col gap-3">
                {canSkip && (
                  <Button
                    color="gray"
                    variant="outlined"
                    onClick={() => {
                      track('auth_mfa_skipped', {
                        redirect: normalizedRedirect,
                      });
                      handleSkipMfa(authStatus, navigate, normalizedRedirect);
                    }}
                  >
                    Skip for now
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Mfa;
